import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { SkipErrorReporting } from '../request-options.ts';
import { parseCommandResult } from './command-result.ts';

export interface InternalAiContextDocument {
  title: string;
  content: string;
}

export interface InternalAiContentResult {
  response: string;
  documentsUsed: string[];
}

export function queryInternalAiContentRequest(
  query: string,
  contextDocuments: InternalAiContextDocument[]
): RequestInfo {
  return {
    url: buildBaseURL({ resourcePath: 'api/v2/internal-ai/content-query' }),
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
    body: JSON.stringify({
      internal_ai_query: {
        query,
        context_documents: contextDocuments,
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

export function parseInternalAiContentResult(content: unknown) {
  return parseCommandResult<InternalAiContentResult>(content);
}

/**
 * The stored recommendation prompt, keyed snake_case because this endpoint
 * renders a plain hash rather than a JSON:API document — the same payload the
 * tio-ai-agent already consumes from `recommendation-prompts/resolve`.
 */
export interface RecommendationPromptBundle {
  main_prompt: string;
  validation_prompt: string;
  identity: string;
  version: number;
  version_id: string;
  set: string;
  checksum: string;
  status: string;
  /** When the version row was created; ISO-8601. */
  created_at?: string;
}

/**
 * Fetches the exact prompt version a recommendation was produced by, by the
 * identity the agent persisted into its context (`db:<set_key>:v<n>`).
 *
 * The identity carries both `:` and `/` — a scoped set_key is
 * `<company_id>/<program_code>` — so it is encoded rather than interpolated
 * raw.
 *
 * `SkipErrorReporting` because every failure here is expected and handled by
 * the caller: the endpoint is internal-staff-only, so a partner admin gets a
 * 403 that must not raise an "Access denied" toast or touch the session, and a
 * withdrawn version 404s as a normal outcome.
 */
export function fetchRecommendationPromptRequest(identity: string): RequestInfo {
  const url = buildBaseURL({ resourcePath: 'api/v2/recommendation-prompts/by-identity' });

  return {
    url: `${url}?identity=${encodeURIComponent(identity)}`,
    method: 'GET',
    headers: new Headers({ Accept: 'application/json' }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}
